import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Tooltip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import usePageTitle from "app/pageTitle";
import MDInput from "theme/components/MDInput";
import MDButton from "theme/components/MDButton";
import axios from "axios.js";

const getWheelPoint = (angle, radius, center = 180) => {
  const radians = ((angle - 90) * Math.PI) / 180;

  return {
    x: center + radius * Math.cos(radians),
    y: center + radius * Math.sin(radians),
  };
};

const getSegmentPath = (startAngle, endAngle) => {
  const start = getWheelPoint(startAngle, 170);
  const end = getWheelPoint(endAngle, 170);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;

  return [
    "M 180 180",
    `L ${start.x} ${start.y}`,
    `A 170 170 0 ${largeArc} 1 ${end.x} ${end.y}`,
    "Z",
  ].join(" ");
};

function WheelPage() {
  const pageTitle = "Spin & Win - Manage Wheel";
  usePageTitle(pageTitle);

  const navigate = useNavigate();
  const { ref } = useParams();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("error");
  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  const [wheel, setWheel] = useState(null);
  const [segments, setSegments] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState(null);

  const [openSegmentDialog, setOpenSegmentDialog] = useState(false);
  const [openDeleteSegmentDialog, setOpenDeleteSegmentDialog] = useState(false);
  const [editingSegment, setEditingSegment] = useState(null);
  const [segmentTitle, setSegmentTitle] = useState("");
  const [segmentQuantity, setSegmentQuantity] = useState("0");
  const [segmentColor, setSegmentColor] = useState("#202124");
  const [segmentType, setSegmentType] = useState("PRIZE");
  const [segmentWinProbability, setSegmentWinProbability] = useState("0");
  const [segmentExtraSpins, setSegmentExtraSpins] = useState("1");
  const [deletingSegment, setDeletingSegment] = useState(null);

  const [openPrizeDialog, setOpenPrizeDialog] = useState(false);
  const [prizeSegment, setPrizeSegment] = useState(null);
  const [editingPrize, setEditingPrize] = useState(null);
  const [prizeTitle, setPrizeTitle] = useState("");
  const [prizeType, setPrizeType] = useState("VOUCHER");
  const [prizeValue, setPrizeValue] = useState("");
  const [prizeFulfilment, setPrizeFulfilment] = useState("MANUAL");

  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const showMessage = (message, messageSeverity = "error") => {
    setSeverity(messageSeverity);
    setErrMsg(message || "An unexpected error occurred.");
    setOpen(true);
  };

  const handleRequestError = () => {
    setShowLoading(false);
    showMessage(
      `Unable to complete request. ${
        navigator.onLine ? "" : "Make sure you have an active internet connection."
      }`
    );
  };

  const fetchWheel = async () => {
    if (!ref) {
      showMessage("Wheel reference is required.");
      return;
    }

    setShowLoading(true);

    try {
      const result = await axios.get(`/portal/tm/spin/promotions/wheels/${ref}/fetch.json`);

      if (result.data.status === "success") {
        const data = result.data.data;
        const wheelData = data.wheel || {};
        const segmentData = Array.isArray(data.segments) ? data.segments : [];

        setWheel(wheelData);
        setSegments(segmentData);

        if (segmentData.length > 0) {
          setSelectedSegment((previous) => {
            if (!previous) {
              return segmentData[0].reference;
            }

            const exists = segmentData.some((segment) => segment.reference === previous);

            return exists ? previous : segmentData[0].reference;
          });
        } else {
          setSelectedSegment(null);
        }

        setPageLoaded(true);
      } else {
        showMessage(result.data.data.message);
      }
    } catch (error) {
      handleRequestError();
      return;
    }

    setShowLoading(false);
  };

  useEffect(() => {
    if (ref) {
      fetchWheel();
    }
  }, [ref, refreshPage]);

  const resetSegmentForm = () => {
    setEditingSegment(null);
    setSegmentTitle("");
    setSegmentQuantity("0");
    setSegmentColor("#202124");
    setSegmentType("PRIZE");
    setSegmentWinProbability("0");
    setSegmentExtraSpins("1");
  };

  const openCreateSegmentDialog = () => {
    resetSegmentForm();
    setOpenSegmentDialog(true);
  };

  const openEditSegmentDialog = (segment) => {
    setEditingSegment(segment);
    setSegmentTitle(segment.title || "");
    setSegmentQuantity(String(segment.quantity ?? 0));
    setSegmentColor(segment.color || "#202124");
    setSegmentType(segment.segment_type || "PRIZE");
    setSegmentWinProbability(String(segment.win_probability ?? 0));
    setSegmentExtraSpins(String(segment.extra_spins ?? 1));
    setOpenSegmentDialog(true);
  };

  const closeSegmentDialog = () => {
    setOpenSegmentDialog(false);
    resetSegmentForm();
  };

  const saveSegment = async () => {
    const title = segmentTitle.trim();
    const quantity = Number(segmentQuantity);
    const winProbability = Number(segmentWinProbability);
    const extraSpins = segmentType === "EXTRA_SPINS" ? Number(segmentExtraSpins) : null;

    if (!title) {
      showMessage("Enter segment title.");
      return;
    }

    if (segmentQuantity.trim() === "" || !Number.isInteger(quantity) || quantity < 0) {
      showMessage("Quantity must be a non-negative whole number.");
      return;
    }

    if (
      segmentWinProbability.trim() === "" ||
      !Number.isFinite(winProbability) ||
      winProbability < 0 ||
      winProbability > 100
    ) {
      showMessage("Win probability must be between 0 and 100.");
      return;
    }

    if (
      segmentType === "EXTRA_SPINS" &&
      (segmentExtraSpins.trim() === "" || !Number.isInteger(extraSpins) || extraSpins < 0)
    ) {
      showMessage("Extra spins must be a non-negative whole number.");
      return;
    }

    const payload = {
      title,
      quantity,
      color: segmentColor,
      segment_type: segmentType,
      win_probability: winProbability,
      extra_spins: extraSpins,
    };

    if (editingSegment) {
      payload.reference = editingSegment.reference;
    } else {
      payload.wheel_reference = ref;
    }

    setOpenSegmentDialog(false);
    setShowLoading(true);

    try {
      const result = await axios.post(
        editingSegment
          ? "/portal/tm/spin/promotions/wheels/segments/edit.json"
          : "/portal/tm/spin/promotions/wheels/segments/create.json",
        payload
      );

      if (result.data.status === "success") {
        showMessage(result.data.data.message, "success");

        if (!editingSegment && result.data.data.reference) {
          setSelectedSegment(result.data.data.reference);
        }

        resetSegmentForm();
        setRefreshPage((previous) => !previous);
      } else {
        showMessage(result.data.data.message);
      }
    } catch (error) {
      handleRequestError();
      return;
    }

    setShowLoading(false);
  };

  const updateSegmentStatus = async (segment) => {
    setShowLoading(true);

    try {
      const result = await axios.get(
        `/portal/tm/spin/promotions/wheels/segments/${segment.reference}/status.json`
      );

      if (result.data.status === "success") {
        showMessage(result.data.data.message, "success");
        setRefreshPage((previous) => !previous);
      } else {
        showMessage(result.data.data.message);
      }
    } catch (error) {
      handleRequestError();
      return;
    }

    setShowLoading(false);
  };

  const deleteSegment = async () => {
    if (!deletingSegment) {
      return;
    }

    setOpenDeleteSegmentDialog(false);
    setShowLoading(true);

    try {
      const result = await axios.post(
        `/portal/tm/spin/promotions/wheels/segments/${deletingSegment.reference}/delete.json`
      );

      if (result.data.status === "success") {
        showMessage(result.data.data.message, "success");

        if (selectedSegment === deletingSegment.reference) {
          setSelectedSegment(null);
        }

        setDeletingSegment(null);
        setRefreshPage((previous) => !previous);
      } else {
        showMessage(result.data.data.message);
      }
    } catch (error) {
      handleRequestError();
      return;
    }

    setShowLoading(false);
  };

  const openPrizeForm = (segment) => {
    const existingPrizes = Array.isArray(segment.prizes) ? segment.prizes : [];

    const existingPrize = existingPrizes.length > 0 ? existingPrizes[0] : null;

    const existingPrizeType = existingPrize?.prize_type || "VOUCHER";

    setPrizeSegment(segment);
    setEditingPrize(existingPrize);
    setPrizeTitle(existingPrize?.title || "");
    setPrizeType(existingPrizeType);

    setPrizeValue(
      existingPrizeType === "PRODUCT" || existingPrizeType === "VOUCHER"
        ? ""
        : existingPrize?.prize_value !== null && existingPrize?.prize_value !== undefined
        ? String(existingPrize.prize_value)
        : ""
    );

    setPrizeFulfilment(
      existingPrizeType === "PRODUCT" ? "MANUAL" : existingPrize?.fulfilment || "MANUAL"
    );

    setOpenPrizeDialog(true);
  };

  const closePrizeDialog = () => {
    setOpenPrizeDialog(false);
    setPrizeSegment(null);
    setEditingPrize(null);
    setPrizeTitle("");
    setPrizeType("VOUCHER");
    setPrizeValue("");
    setPrizeFulfilment("MANUAL");
  };

  const handlePrizeTypeChange = (value) => {
    setPrizeType(value);

    if (value === "PRODUCT" || value === "VOUCHER") {
      setPrizeValue("");
    }

    if (value === "PRODUCT") {
      setPrizeFulfilment("MANUAL");
    }
  };

  const savePrize = async () => {
    const title = prizeTitle.trim();
    const prizeTypeValue = prizeType.trim();

    const valueRequired = prizeTypeValue !== "PRODUCT" && prizeTypeValue !== "VOUCHER";

    const fulfilment = prizeTypeValue === "PRODUCT" ? "MANUAL" : prizeFulfilment.trim();

    if (!prizeSegment) {
      showMessage("Select a segment for this prize.");
      return;
    }

    if (!title) {
      showMessage("Enter a prize title.");
      return;
    }

    if (!prizeTypeValue) {
      showMessage("Select a prize type.");
      return;
    }

    if (prizeTypeValue !== "PRODUCT" && !fulfilment) {
      showMessage("Select the prize fulfilment method.");
      return;
    }

    if (
      valueRequired &&
      (prizeValue.trim() === "" || !Number.isFinite(Number(prizeValue)) || Number(prizeValue) < 0)
    ) {
      showMessage("Enter a valid, non-negative prize value.");
      return;
    }

    const payload = {
      title,
      prize_type: prizeTypeValue,
      prize_value: valueRequired ? Number(prizeValue) : null,
      fulfilment,
    };

    if (editingPrize) {
      payload.reference = editingPrize.reference;
    } else {
      payload.wheel_reference = ref;
      payload.segment_reference = prizeSegment.reference;
    }

    setOpenPrizeDialog(false);
    setShowLoading(true);

    try {
      const result = await axios.post(
        editingPrize
          ? "/portal/tm/spin/promotions/wheels/segments/prizes/edit.json"
          : "/portal/tm/spin/promotions/wheels/segments/prizes/create.json",
        payload
      );

      if (result.data.status === "success") {
        showMessage(result.data.data.message, "success");
        setRefreshPage((previous) => !previous);
      } else {
        showMessage(result.data.data.message);
      }
    } catch (error) {
      handleRequestError();
      return;
    }

    setShowLoading(false);
    closePrizeDialog();
  };

  const getSegmentColor = (segment, index) => {
    if (segment.color && /^#[0-9A-Fa-f]{6}$/.test(segment.color)) {
      return segment.color;
    }

    const defaultColors = [
      "#202124",
      "#D9A441",
      "#3B6F70",
      "#8C4141",
      "#526A8D",
      "#9B6A9D",
      "#557C55",
      "#B87545",
    ];

    return defaultColors[index % defaultColors.length];
  };

  const getSegmentType = (segment) => {
    const type = (segment.segment_type || "").toUpperCase();

    if (type === "PRIZE") {
      return "Prize";
    }

    if (type === "NO_WIN") {
      return "No Win";
    }

    if (type === "EXTRA_SPINS") {
      return "Extra Spins";
    }

    return segment.segment_type || "Segment";
  };

  const getSegmentDescription = (segment) => {
    const type = (segment.segment_type || "").toUpperCase();

    if (type === "NO_WIN") {
      return "No prize";
    }

    if (type === "EXTRA_SPINS") {
      return `${segment.extra_spins || 0} extra spins`;
    }

    if (Array.isArray(segment.prizes) && segment.prizes.length > 0) {
      return segment.prizes.map((prize) => prize.title).join(", ");
    }

    return "No prize configured";
  };

  const getWheelLabel = (segment) => {
    const type = (segment.segment_type || "").toUpperCase();

    if (type === "NO_WIN") {
      return segment.title || "No Win";
    }

    if (type === "EXTRA_SPINS") {
      return `+${segment.extra_spins || 0} Spins`;
    }

    return segment.title || "Segment";
  };

  const activeSegments = segments.filter((segment) => segment.status);
  const selectedSegmentData = segments.find((segment) => segment.reference === selectedSegment);

  const renderWheel = () => {
    if (segments.length === 0) {
      return (
        <MDBox
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          sx={{
            width: "100%",
            minHeight: 360,
          }}
        >
          <Icon sx={{ fontSize: 48, color: "text.disabled" }}>casino</Icon>

          <MDTypography mt={2} variant="button" color="text">
            Your wheel will appear here
          </MDTypography>

          <MDTypography mt={0.5} variant="caption" color="text" textAlign="center">
            Add segments to build the wheel.
          </MDTypography>
        </MDBox>
      );
    }

    const segmentAngle = 360 / segments.length;

    return (
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{
          width: "100%",
          maxWidth: 440,
          mx: "auto",
          py: 2,
        }}
      >
        <svg
          viewBox="0 0 360 360"
          role="img"
          aria-label={`${wheel?.title || "Prize"} wheel preview`}
          style={{
            width: "100%",
            height: "auto",
            overflow: "visible",
          }}
        >
          <circle cx="180" cy="180" r="176" fill="#FFFFFF" stroke="#DADADA" strokeWidth="2" />

          {segments.map((segment, index) => {
            const startAngle = index * segmentAngle;
            const endAngle = (index + 1) * segmentAngle;
            const middleAngle = startAngle + segmentAngle / 2;
            const labelPoint = getWheelPoint(middleAngle, 112);
            const isSelected = segment.reference === selectedSegment;
            const color = getSegmentColor(segment, index);

            return (
              <g
                key={segment.reference || index}
                onClick={() => setSelectedSegment(segment.reference)}
                style={{ cursor: "pointer" }}
              >
                <path
                  d={getSegmentPath(startAngle, endAngle)}
                  fill={color}
                  stroke="#FFFFFF"
                  strokeWidth={isSelected ? 4 : 2}
                  opacity={segment.status ? 1 : 0.35}
                />

                <text
                  x={labelPoint.x}
                  y={labelPoint.y}
                  transform={`rotate(${middleAngle}, ${labelPoint.x}, ${labelPoint.y})`}
                  fill="#FFFFFF"
                  fontSize={segments.length > 8 ? 8 : segments.length > 5 ? 10 : 12}
                  fontWeight="600"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  style={{ pointerEvents: "none" }}
                >
                  {getWheelLabel(segment).length > 18
                    ? `${getWheelLabel(segment).substring(0, 16)}...`
                    : getWheelLabel(segment)}
                </text>
              </g>
            );
          })}

          <circle cx="180" cy="180" r="39" fill="#FFFFFF" stroke="#DADADA" strokeWidth="2" />

          <circle cx="180" cy="180" r="31" fill="#202124" />

          <text
            x="180"
            y="176"
            fill="#FFFFFF"
            fontSize="11"
            fontWeight="700"
            textAnchor="middle"
            dominantBaseline="middle"
          >
            SPIN
          </text>

          <text
            x="180"
            y="190"
            fill="#FFFFFF"
            fontSize="8"
            textAnchor="middle"
            dominantBaseline="middle"
          >
            & WIN
          </text>

          <path d="M 180 2 L 168 22 L 192 22 Z" fill="#202124" stroke="#FFFFFF" strokeWidth="1" />
        </svg>
      </MDBox>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <Dialog open={openSegmentDialog} fullWidth maxWidth="sm" onClose={closeSegmentDialog}>
        <DialogTitle>{editingSegment ? "Edit Segment" : "Create Segment"}</DialogTitle>

        <DialogContent>
          <MDBox>
            <MDBox mt={1}>
              <MDInput
                name="title"
                margin="dense"
                label="Segment Title"
                type="text"
                fullWidth
                variant="outlined"
                value={segmentTitle}
                onChange={(event) => setSegmentTitle(event.target.value)}
              />
            </MDBox>

            <MDBox mt={2}>
              <MDInput
                name="segment_type"
                margin="dense"
                label="Segment Type"
                select
                fullWidth
                variant="outlined"
                value={segmentType}
                onChange={(event) => setSegmentType(event.target.value)}
                SelectProps={{
                  native: true,
                }}
              >
                <option value="PRIZE">Prize</option>
                <option value="NO_WIN">No Win</option>
                <option value="EXTRA_SPINS">Extra Spins</option>
              </MDInput>
            </MDBox>

            <MDBox mt={2}>
              <MDInput
                name="quantity"
                margin="dense"
                label="Quantity"
                type="number"
                fullWidth
                variant="outlined"
                value={segmentQuantity}
                onChange={(event) => setSegmentQuantity(event.target.value)}
                inputProps={{
                  min: 0,
                  step: 1,
                }}
              />
            </MDBox>

            <MDBox mt={2}>
              <MDInput
                name="win_probability"
                margin="dense"
                label="Win Probability (%)"
                type="number"
                fullWidth
                variant="outlined"
                value={segmentWinProbability}
                onChange={(event) => setSegmentWinProbability(event.target.value)}
                inputProps={{
                  min: 0,
                  max: 100,
                  step: "any",
                }}
              />
            </MDBox>

            {segmentType === "EXTRA_SPINS" && (
              <MDBox mt={2}>
                <MDInput
                  name="extra_spins"
                  margin="dense"
                  label="Extra Spins"
                  type="number"
                  fullWidth
                  variant="outlined"
                  value={segmentExtraSpins}
                  onChange={(event) => setSegmentExtraSpins(event.target.value)}
                  inputProps={{
                    min: 0,
                    step: 1,
                  }}
                />
              </MDBox>
            )}

            <MDBox mt={2}>
              <MDTypography variant="button" fontWeight="medium">
                Segment Color
              </MDTypography>

              <MDBox mt={1} display="flex" alignItems="center" gap={2}>
                <input
                  type="color"
                  value={segmentColor}
                  onChange={(event) => setSegmentColor(event.target.value)}
                  style={{
                    width: 48,
                    height: 40,
                    padding: 2,
                    cursor: "pointer",
                    border: "1px solid #DADADA",
                    borderRadius: 4,
                    background: "transparent",
                  }}
                />

                <MDInput
                  name="color"
                  margin="dense"
                  label="Hex Color"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={segmentColor}
                  onChange={(event) => setSegmentColor(event.target.value)}
                />
              </MDBox>
            </MDBox>
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton onClick={closeSegmentDialog} color="dark" variant="outlined">
            Cancel
          </MDButton>

          <MDButton color="dark" variant="contained" onClick={saveSegment}>
            {editingSegment ? "Save Changes" : "Create Segment"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openPrizeDialog} fullWidth maxWidth="sm" onClose={closePrizeDialog}>
        <DialogTitle>{editingPrize ? "Edit Prize" : "Set Prize"}</DialogTitle>

        <DialogContent>
          <MDBox mt={1}>
            <MDTypography variant="caption" color="text">
              Segment
            </MDTypography>

            <MDTypography variant="button" fontWeight="medium" display="block">
              {prizeSegment?.title || ""}
            </MDTypography>
          </MDBox>

          <MDBox mt={2}>
            <MDInput
              name="prize_title"
              margin="dense"
              label="Prize Title"
              type="text"
              fullWidth
              variant="outlined"
              value={prizeTitle}
              onChange={(event) => setPrizeTitle(event.target.value)}
            />
          </MDBox>

          <MDBox mt={2}>
            <MDInput
              name="prize_type"
              margin="dense"
              label="Prize Type"
              select
              fullWidth
              variant="outlined"
              value={prizeType}
              onChange={(event) => handlePrizeTypeChange(event.target.value)}
              SelectProps={{
                native: true,
              }}
            >
              <option value="AIRTIME">Airtime</option>
              {/* <option value="DATA">Data</option> */}
              <option value="CASH">Cash</option>
              <option value="VOUCHER">Voucher</option>
              <option value="PRODUCT">Product</option>
            </MDInput>
          </MDBox>

          {prizeType !== "PRODUCT" && prizeType !== "VOUCHER" && (
            <MDBox mt={2}>
              <MDInput
                name="prize_value"
                margin="dense"
                label="Prize Value"
                type="number"
                fullWidth
                variant="outlined"
                value={prizeValue}
                onChange={(event) => setPrizeValue(event.target.value)}
                inputProps={{
                  min: 0,
                  step: "any",
                }}
              />
            </MDBox>
          )}

          {prizeType !== "PRODUCT" && (
            <MDBox mt={2}>
              <MDInput
                name="fulfilment"
                margin="dense"
                label="Fulfilment Method"
                select
                fullWidth
                variant="outlined"
                value={prizeFulfilment}
                onChange={(event) => setPrizeFulfilment(event.target.value)}
                SelectProps={{
                  native: true,
                }}
              >
                <option value="MANUAL">Manual</option>
                <option value="AUTO">Automated</option>
              </MDInput>
            </MDBox>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton onClick={closePrizeDialog} color="dark" variant="outlined">
            Cancel
          </MDButton>

          <MDButton color="dark" variant="contained" onClick={savePrize}>
            {editingPrize ? "Save Changes" : "Set Prize"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteSegmentDialog}
        fullWidth
        maxWidth="xs"
        onClose={() => {
          setOpenDeleteSegmentDialog(false);
          setDeletingSegment(null);
        }}
      >
        <DialogTitle>Delete Segment</DialogTitle>

        <DialogContent>
          <MDTypography variant="button" color="text">
            Are you sure you want to delete <strong>{deletingSegment?.title}</strong>?
          </MDTypography>
        </DialogContent>

        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenDeleteSegmentDialog(false);
              setDeletingSegment(null);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>

          <MDButton color="error" variant="contained" onClick={deleteSegment}>
            Delete
          </MDButton>
        </DialogActions>
      </Dialog>

      <MDBox mt={1} mb={5}>
        {pageLoaded && wheel && (
          <MDBox>
            <Card>
              <MDBox
                p={3}
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                flexWrap="wrap"
                gap={2}
              >
                <MDBox>
                  <MDTypography variant="h5" fontWeight="bold">
                    {wheel.title}
                  </MDTypography>

                  <MDTypography mt={1} variant="button" fontWeight="light" color="text">
                    {wheel.description || "Configure the segments on your wheel."}
                  </MDTypography>

                  <MDBox mt={2} display="flex" alignItems="center" gap={1}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      color={wheel.status ? "success" : "text"}
                    >
                      {wheel.status ? "Active" : "Inactive"}
                    </MDTypography>

                    <MDTypography variant="caption" color="text">
                      | {segments.length} segments
                    </MDTypography>
                  </MDBox>
                </MDBox>

                <MDBox display="flex" gap={1} flexWrap="wrap">
                  <MDButton
                    variant="outlined"
                    color="dark"
                    onClick={() => navigate(-1)}
                    startIcon={<Icon>arrow_back</Icon>}
                    size="small"
                  >
                    Back
                  </MDButton>
                  <MDButton
                    variant="outlined"
                    color="dark"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(wheel.shareLink);
                        showMessage("Wheel link copied to clipboard.", "success");
                      } catch (error) {
                        showMessage("Unable to copy the wheel link.");
                      }
                    }}
                    startIcon={<Icon>content_copy</Icon>}
                    size="small"
                  >
                    Share Link
                  </MDButton>
                  <MDButton
                    variant="outlined"
                    color="info"
                    onClick={() => navigate(`/tm/spin/promotions/wheels/participants/${ref}`)}
                    startIcon={<Icon>people</Icon>}
                    size="small"
                  >
                    Participants
                  </MDButton>

                  <MDButton
                    variant="outlined"
                    color="success"
                    onClick={() => navigate(`/tm/spin/promotions/wheels/spins/${ref}`)}
                    startIcon={<Icon>casino</Icon>}
                    size="small"
                  >
                    Spins
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>

            <MDBox
              mt={3}
              display="flex"
              flexDirection={{ xs: "column", lg: "row" }}
              alignItems="stretch"
              gap={3}
            >
              <MDBox
                sx={{
                  width: { xs: "100%", lg: "50%" },
                  minWidth: 0,
                }}
              >
                <Card sx={{ height: "100%" }}>
                  <MDBox
                    p={2}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="start"
                    gap={1}
                  >
                    <MDBox>
                      <MDTypography variant="h6" fontWeight="medium">
                        Wheel Segments
                      </MDTypography>

                      <MDTypography variant="caption" color="text">
                        Configure the segments available on this wheel.
                      </MDTypography>
                    </MDBox>

                    <MDButton
                      variant="contained"
                      color="dark"
                      size="small"
                      onClick={openCreateSegmentDialog}
                      startIcon={<Icon>add</Icon>}
                    >
                      Add
                    </MDButton>
                  </MDBox>

                  <Divider />

                  {segments.length > 0 ? (
                    <MDBox p={2} display="flex" flexDirection="column" gap={1.5}>
                      {segments.map((segment, index) => {
                        const isSelected = segment.reference === selectedSegment;
                        const color = getSegmentColor(segment, index);

                        return (
                          <MDBox
                            key={segment.reference || index}
                            onClick={() => setSelectedSegment(segment.reference)}
                            sx={{
                              p: 2,
                              border: "1px solid",
                              borderColor: isSelected ? "dark.main" : "grey.300",
                              borderRadius: 1,
                              cursor: "pointer",
                              backgroundColor: isSelected ? "action.selected" : "background.paper",
                              transition: "border-color 0.15s ease",
                              "&:hover": {
                                borderColor: "text.primary",
                              },
                            }}
                          >
                            <MDBox
                              display="flex"
                              alignItems="flex-start"
                              justifyContent="space-between"
                              gap={2}
                            >
                              <MDBox
                                display="flex"
                                alignItems="flex-start"
                                gap={1.5}
                                sx={{ minWidth: 0, flex: 1 }}
                              >
                                <MDBox
                                  sx={{
                                    width: 18,
                                    height: 18,
                                    minWidth: 18,
                                    mt: 0.3,
                                    borderRadius: "50%",
                                    backgroundColor: color,
                                    border: "1px solid",
                                    borderColor: "divider",
                                  }}
                                />

                                <MDBox sx={{ minWidth: 0 }}>
                                  <MDTypography variant="button" fontWeight="medium">
                                    {segment.title}
                                  </MDTypography>

                                  <MDTypography display="block" variant="caption" color="text">
                                    {getSegmentType(segment)}
                                  </MDTypography>

                                  <MDTypography
                                    mt={0.5}
                                    display="block"
                                    variant="caption"
                                    color="text"
                                  >
                                    {getSegmentDescription(segment)}
                                  </MDTypography>
                                </MDBox>
                              </MDBox>

                              <MDBox
                                display="flex"
                                flexDirection="column"
                                alignItems="flex-end"
                                gap={0.5}
                              >
                                <MDTypography
                                  variant="caption"
                                  fontWeight="medium"
                                  color={segment.status ? "success" : "text"}
                                >
                                  {segment.status ? "Active" : "Inactive"}
                                </MDTypography>

                                <MDTypography variant="caption" color="text">
                                  Qty: {segment.quantity ?? 0}
                                </MDTypography>
                              </MDBox>
                            </MDBox>

                            <MDBox
                              mt={1.5}
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              flexWrap="wrap"
                              gap={1}
                            >
                              <MDTypography variant="caption" color="text">
                                Win probability: {segment.win_probability ?? 0}%
                              </MDTypography>

                              <MDTypography variant="caption" color="text">
                                {segment.color || color}
                              </MDTypography>
                            </MDBox>

                            <Divider sx={{ my: 1.5 }} />

                            <MDBox
                              display="flex"
                              justifyContent="flex-end"
                              alignItems="center"
                              gap={1}
                              onClick={(event) => event.stopPropagation()}
                            >
                              {segment.segment_type === "PRIZE" && (
                                <Tooltip
                                  title={
                                    Array.isArray(segment.prizes) && segment.prizes.length > 0
                                      ? "Edit Prize"
                                      : "Set Prize"
                                  }
                                >
                                  <MDButton
                                    size="small"
                                    variant="outlined"
                                    color="dark"
                                    iconOnly
                                    onClick={() => openPrizeForm(segment)}
                                  >
                                    <Icon fontSize="small">card_giftcard</Icon>
                                  </MDButton>
                                </Tooltip>
                              )}

                              <Tooltip title="Edit Segment">
                                <MDButton
                                  size="small"
                                  variant="outlined"
                                  color="info"
                                  iconOnly
                                  onClick={() => openEditSegmentDialog(segment)}
                                >
                                  <Icon fontSize="small">edit</Icon>
                                </MDButton>
                              </Tooltip>

                              <Tooltip
                                title={segment.status ? "Deactivate Segment" : "Activate Segment"}
                              >
                                <MDButton
                                  size="small"
                                  variant="outlined"
                                  color={segment.status ? "warning" : "success"}
                                  iconOnly
                                  onClick={() => updateSegmentStatus(segment)}
                                >
                                  <Icon fontSize="small">
                                    {segment.status ? "pause" : "play_arrow"}
                                  </Icon>
                                </MDButton>
                              </Tooltip>

                              <Tooltip title="Delete Segment">
                                <MDButton
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  iconOnly
                                  onClick={() => {
                                    setDeletingSegment(segment);
                                    setOpenDeleteSegmentDialog(true);
                                  }}
                                >
                                  <Icon fontSize="small">delete</Icon>
                                </MDButton>
                              </Tooltip>
                            </MDBox>
                          </MDBox>
                        );
                      })}
                    </MDBox>
                  ) : (
                    <MDBox p={4} textAlign="center">
                      <Icon sx={{ fontSize: 40, color: "text.disabled" }}>casino</Icon>

                      <MDTypography mt={1} variant="button" color="text">
                        No segments have been added.
                      </MDTypography>

                      <MDBox mt={2}>
                        <MDButton
                          variant="contained"
                          color="dark"
                          size="small"
                          startIcon={<Icon>add</Icon>}
                          onClick={openCreateSegmentDialog}
                        >
                          Add First Segment
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  )}
                </Card>
              </MDBox>

              <MDBox
                sx={{
                  width: { xs: "100%", lg: "50%" },
                  minWidth: 0,
                }}
              >
                <Card sx={{ height: "100%" }}>
                  <MDBox p={2}>
                    <MDTypography variant="h6" fontWeight="medium">
                      Wheel Preview
                    </MDTypography>

                    <MDTypography variant="caption" color="text">
                      Select a segment to highlight it on the wheel.
                    </MDTypography>
                  </MDBox>

                  <Divider />

                  <MDBox px={2} pb={2}>
                    {renderWheel()}

                    <MDBox
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      gap={2}
                      flexWrap="wrap"
                    >
                      <MDBox display="flex" alignItems="center" gap={0.75}>
                        <MDBox
                          sx={{
                            width: 9,
                            height: 9,
                            borderRadius: "50%",
                            backgroundColor: "success.main",
                          }}
                        />

                        <MDTypography variant="caption" color="text">
                          Active: {activeSegments.length}
                        </MDTypography>
                      </MDBox>

                      <MDBox display="flex" alignItems="center" gap={0.75}>
                        <MDBox
                          sx={{
                            width: 9,
                            height: 9,
                            borderRadius: "50%",
                            backgroundColor: "text.disabled",
                          }}
                        />

                        <MDTypography variant="caption" color="text">
                          Inactive: {segments.length - activeSegments.length}
                        </MDTypography>
                      </MDBox>
                    </MDBox>

                    {selectedSegmentData && (
                      <MDBox
                        mt={3}
                        p={2}
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                        }}
                      >
                        <MDTypography variant="caption" color="text">
                          SELECTED SEGMENT
                        </MDTypography>

                        <MDBox mt={1} display="flex" alignItems="center" gap={1}>
                          <MDBox
                            sx={{
                              width: 14,
                              height: 14,
                              minWidth: 14,
                              borderRadius: "50%",
                              backgroundColor: getSegmentColor(
                                selectedSegmentData,
                                segments.findIndex(
                                  (segment) => segment.reference === selectedSegment
                                )
                              ),
                            }}
                          />

                          <MDTypography variant="button" fontWeight="medium">
                            {selectedSegmentData.title}
                          </MDTypography>
                        </MDBox>

                        <MDBox mt={1.5} display="flex" flexWrap="wrap" gap={3}>
                          <MDBox>
                            <MDTypography variant="caption" color="text">
                              Type
                            </MDTypography>

                            <MDTypography variant="button" display="block">
                              {getSegmentType(selectedSegmentData)}
                            </MDTypography>
                          </MDBox>

                          <MDBox>
                            <MDTypography variant="caption" color="text">
                              Quantity
                            </MDTypography>

                            <MDTypography variant="button" display="block">
                              {selectedSegmentData.quantity ?? 0}
                            </MDTypography>
                          </MDBox>

                          <MDBox>
                            <MDTypography variant="caption" color="text">
                              Win Probability
                            </MDTypography>

                            <MDTypography variant="button" display="block">
                              {selectedSegmentData.win_probability ?? 0}%
                            </MDTypography>
                          </MDBox>
                        </MDBox>

                        {selectedSegmentData.segment_type === "EXTRA_SPINS" && (
                          <MDBox mt={2}>
                            <MDTypography variant="caption" color="text">
                              Extra spins awarded
                            </MDTypography>

                            <MDTypography variant="button" fontWeight="medium" display="block">
                              {selectedSegmentData.extra_spins || 0}
                            </MDTypography>
                          </MDBox>
                        )}

                        {selectedSegmentData.segment_type === "PRIZE" && (
                          <MDBox mt={2}>
                            {Array.isArray(selectedSegmentData.prizes) &&
                            selectedSegmentData.prizes.length > 0 ? (
                              selectedSegmentData.prizes.map((prize) => (
                                <MDBox
                                  key={prize.reference}
                                  mb={1}
                                  p={1.5}
                                  sx={{
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 1,
                                  }}
                                >
                                  <MDTypography
                                    variant="button"
                                    fontWeight="medium"
                                    display="block"
                                  >
                                    {prize.title}
                                  </MDTypography>

                                  <MDTypography variant="caption" color="text" display="block">
                                    {prize.prize_type || ""}
                                    {prize.prize_value !== null && prize.prize_value !== undefined
                                      ? ` · ${prize.prize_value}`
                                      : ""}
                                  </MDTypography>

                                  <MDTypography variant="caption" color="text" display="block">
                                    Fulfilment: {prize.fulfilment || "-"}
                                  </MDTypography>
                                </MDBox>
                              ))
                            ) : (
                              <MDTypography variant="caption" color="text" display="block">
                                No prize has been configured for this segment.
                              </MDTypography>
                            )}

                            <MDBox mt={1}>
                              <MDButton
                                variant="outlined"
                                color="dark"
                                size="small"
                                startIcon={<Icon>card_giftcard</Icon>}
                                onClick={() => openPrizeForm(selectedSegmentData)}
                              >
                                {Array.isArray(selectedSegmentData.prizes) &&
                                selectedSegmentData.prizes.length > 0
                                  ? "Edit Prize"
                                  : "Set Prize"}
                              </MDButton>
                            </MDBox>
                          </MDBox>
                        )}
                      </MDBox>
                    )}
                  </MDBox>
                </Card>
              </MDBox>
            </MDBox>
          </MDBox>
        )}
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default WheelPage;
